import { Briefcase, Building2, Layers, Paintbrush, UserRound, Check, ChevronsUpDown, Languages, LogOut, Menu, Monitor, Moon, Search, Sun } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Kbd } from '@/components/ui/Kbd'
import { MenuItem, MenuLabel, MenuSeparator, Popover } from '@/components/ui/Popover'
import { toApiError } from '@/api/errors'
import { cn } from '@/lib/cn'
import { pickName } from '@/lib/format'
import { useStores } from '@/stores'
import type { ThemePreference } from '@/stores/UiStore'
import { LOCALE_NAMES, useT, type TKey } from '@/i18n'
import { CONTENT_LOCALES } from '@/types/api'
import { isOpenable } from './access'
import { entryVisible, flatten, itemsByPath, menuText } from './menuModel'
import { GlobalRefresh } from './GlobalRefresh'
import { ChatButton } from '@/features/chat/ChatDrawer'
import { NotificationBell } from './NotificationBell'
import { OnlineUsers } from './OnlineUsers'
import { MyAccountDialog } from './MyAccountDialog'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/**
 * «Баланс» (client alfi.uz, 2026-10-23, as their AFF/ChF toggle): procurement,
 * receiving and finance lists show that balance's documents and new ones
 * start with its firms and products. Orders, shipments, returns and
 * transfers are shared and ignore it. «Все» shows everything.
 */
const BalanceSwitcher = observer(function BalanceSwitcher() {
  const t = useT()
  const { session } = useStores()
  const groups = session.balanceGroups
  const active = session.activeBalanceGroup
  if (session.balanceFixed) {
    // A trader's or logist's account: its own balance, no switching.
    return (
      <span
        title={t('layout.balance.fixed')}
        className="hidden h-8.5 max-w-[200px] items-center gap-2 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(13.5px*var(--text-scale))] text-fg-2 md:flex"
      >
        <Layers className="size-4 shrink-0 text-muted" strokeWidth={1.85} />
        <span className="truncate">{active ? `${active.code} · ${active.name}` : t('layout.balance.title')}</span>
      </span>
    )
  }
  if (groups.length < 2) return null
  return (
    <Popover
      align="end"
      trigger={({ toggle, ...aria }) => (
        <button
          type="button"
          onClick={() => toggle()}
          {...aria}
          aria-label={t('layout.balance.title')}
          title={t('layout.balance.hint')}
          className="hidden h-8.5 max-w-[180px] items-center gap-2 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(13.5px*var(--text-scale))] text-fg-2 transition-colors hover:bg-hover hover:text-fg md:flex"
        >
          <Layers className="size-4 shrink-0 text-muted" strokeWidth={1.85} />
          <span className="truncate">{active ? active.code : t('layout.balance.all')}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-subtle" />
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <MenuLabel>{t('layout.balance.title')}</MenuLabel>
          <MenuItem
            onSelect={() => {
              session.setActiveBalanceGroup('')
              close()
            }}
            trailing={!active ? <Check className="size-3.5 text-accent" /> : undefined}
          >
            {t('layout.balance.all')}
          </MenuItem>
          {groups.map((group) => (
            <MenuItem
              key={group.id}
              onSelect={() => {
                session.setActiveBalanceGroup(group.id)
                close()
              }}
              trailing={group.id === active?.id ? <Check className="size-3.5 text-accent" /> : undefined}
            >
              <span className="truncate">
                {group.code} · {group.name}
              </span>
            </MenuItem>
          ))}
          <p className="px-2.5 pb-1 pt-2 text-[length:calc(12px*var(--text-scale))] leading-snug text-muted">{t('layout.balance.note')}</p>
        </div>
      )}
    </Popover>
  )
})

const BranchSwitcher = observer(function BranchSwitcher() {
  const t = useT()
  const { session, ui } = useStores()
  const branches = session.branches
  const active = session.activeBranch

  // A company-wide sign-in has no branch to pick: nothing to show (the account menu names the company).
  if (branches.length === 0) return null

  return (
    <Popover
      align="end"
      trigger={({ toggle, ...aria }) => (
        <button
          type="button"
          onClick={() => toggle()}
          {...aria}
          aria-label={t('layout.topbar.activeBranch')}
          className="hidden h-8.5 max-w-[220px] items-center gap-2 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(13.5px*var(--text-scale))] text-fg-2 transition-colors hover:bg-hover hover:text-fg md:flex"
        >
          <Building2 className="size-4 shrink-0 text-muted" strokeWidth={1.85} />
          <span className="truncate">{active ? pickName(active, ui.contentLocale) : t('layout.topbar.selectBranch')}</span>
          {branches.length > 1 && <ChevronsUpDown className="size-3.5 shrink-0 text-subtle" />}
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <MenuLabel>{t('layout.topbar.workingBranch')}</MenuLabel>
          {branches.map((branch) => (
            <MenuItem
              key={branch.id}
              onSelect={() => {
                session.setActiveBranch(branch.id)
                close()
              }}
              trailing={branch.id === session.activeBranchId ? <Check className="size-3.5 text-accent" /> : undefined}
            >
              <span className="flex items-baseline gap-2">
                {pickName(branch, ui.contentLocale)}
                <span className="num text-[length:calc(11.5px*var(--text-scale))] text-subtle">{branch.code}</span>
              </span>
            </MenuItem>
          ))}
          <p className="px-2.5 pb-1 pt-2 text-[length:calc(12px*var(--text-scale))] leading-snug text-muted">
            {t('layout.topbar.branchNote')}
          </p>
        </div>
      )}
    </Popover>
  )
})

/**
 * The company this sign-in works in, and the linked companies it can move to
 * (same login and password). Shown only when switching is possible; a switch
 * reloads the app on the home route (AuthStore.switchCompany).
 */
const CompanySwitcher = observer(function CompanySwitcher() {
  const t = useT()
  const { session, auth, ui, toasts } = useStores()
  const [switching, setSwitching] = useState<string | null>(null)
  const current = session.currentCompany
  if (!session.canSwitchCompany || !current) return null

  const choose = async (companyId: string, close: () => void) => {
    if (switching) return
    setSwitching(companyId)
    try {
      await auth.switchCompany(companyId)
    } catch (error) {
      setSwitching(null)
      close()
      const apiError = toApiError(error)
      if (apiError.code === 'COMPANY_NOT_LINKED') {
        session.forgetCompany(companyId)
        toasts.error(t('layout.company.notLinked'))
      } else if (apiError.code === 'COMPANY_SWITCH_STALE') {
        toasts.error(t('layout.company.stale'))
        // Leave the notice readable for a moment: sign-out reloads the page.
        window.setTimeout(() => void auth.logout(), 2500)
      } else {
        toasts.error(t('layout.company.failed'), apiError.message)
      }
    }
  }

  return (
    <Popover
      align="end"
      trigger={({ toggle, ...aria }) => (
        <button
          type="button"
          onClick={() => toggle()}
          {...aria}
          aria-label={t('layout.company.current')}
          title={t('layout.company.current')}
          className="hidden h-8.5 max-w-[220px] items-center gap-2 rounded-[calc(10px*var(--radius-scale))] px-2.5 text-[length:calc(13.5px*var(--text-scale))] text-fg-2 transition-colors hover:bg-hover hover:text-fg md:flex"
        >
          <Briefcase className="size-4 shrink-0 text-muted" strokeWidth={1.85} />
          <span className="truncate">{pickName(current, ui.contentLocale)}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-subtle" />
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <MenuLabel>{t('layout.company.title')}</MenuLabel>
          {session.companies.map((company) => (
            <MenuItem
              key={company.id}
              disabled={Boolean(switching) || (!company.is_current && !company.available)}
              onSelect={() => {
                if (company.is_current) close()
                else void choose(company.id, close)
              }}
              trailing={
                company.is_current ? (
                  <Check className="size-3.5 text-accent" />
                ) : switching === company.id ? (
                  <span className="text-[length:calc(11.5px*var(--text-scale))] text-muted">{t('layout.company.switching')}</span>
                ) : !company.available ? (
                  <span className="text-[length:calc(11.5px*var(--text-scale))] text-muted">{t('layout.company.unavailable')}</span>
                ) : undefined
              }
            >
              <span className="truncate">{pickName(company, ui.contentLocale)}</span>
            </MenuItem>
          ))}
          <p className="px-2.5 pb-1 pt-2 text-[length:calc(12px*var(--text-scale))] leading-snug text-muted">{t('layout.company.note')}</p>
        </div>
      )}
    </Popover>
  )
})

const themeOptions: { value: ThemePreference; label: TKey; icon: typeof Sun }[] = [
  { value: 'light', label: 'layout.topbar.themeLight', icon: Sun },
  { value: 'dark', label: 'layout.topbar.themeDark', icon: Moon },
  { value: 'system', label: 'layout.topbar.themeSystem', icon: Monitor },
]

/**
 * Light / dark / system as one segmented switch: a single pill glides to the
 * choice, then the interface cross-fades to the new theme. Running both at
 * once let the view-transition snapshot freeze the pill mid-way.
 */
const ThemeSwitch = observer(function ThemeSwitch() {
  const t = useT()
  const { ui } = useStores()
  const [pending, setPending] = useState<ThemePreference | null>(null)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const current = pending ?? ui.theme
  const index = themeOptions.findIndex((option) => option.value === current)

  const choose = (value: ThemePreference) => {
    if (value === current) return
    setPending(value)
    window.clearTimeout(timer.current)
    // Let the pill land first (its slide lasts ~220 ms), then switch the theme.
    timer.current = window.setTimeout(() => {
      ui.setTheme(value)
      setPending(null)
    }, 200)
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('layout.topbar.appearance')}
      className="relative mx-1 mb-1 grid grid-cols-3 rounded-[calc(10px*var(--radius-scale))] bg-hover p-0.5"
      onKeyDown={(event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
        event.preventDefault()
        const next = themeOptions[(index + (event.key === 'ArrowRight' ? 1 : themeOptions.length - 1)) % themeOptions.length]
        choose(next.value)
        event.currentTarget.querySelector<HTMLElement>(`[data-value="${next.value}"]`)?.focus()
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc((100%-4px)/3)] rounded-[calc(8px*var(--radius-scale))] bg-glass-solid shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_var(--glass-highlight)] transition-transform duration-[220ms] ease-[var(--ease-spring)]"
        style={{ transform: `translateX(${Math.max(0, index) * 100}%)` }}
      />
      {themeOptions.map((option) => {
        const selected = option.value === current
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            data-value={option.value}
            onClick={() => choose(option.value)}
            className={cn(
              'relative z-[1] flex h-7 items-center justify-center gap-1.5 rounded-[calc(8px*var(--radius-scale))] text-[length:calc(12.5px*var(--text-scale))] transition-colors duration-200',
              selected ? 'text-fg' : 'text-muted hover:text-fg',
            )}
          >
            <option.icon className="size-3.5" />
            {t(option.label)}
          </button>
        )
      })}
    </div>
  )
})

const UserMenu = observer(function UserMenu() {
  const t = useT()
  const navigate = useNavigate()
  const { session, auth, ui } = useStores()
  // Bumped on every opening: «Мой профиль» remounts with the saved card.
  const [accountDialog, setAccountDialog] = useState({ open: false, key: 0 })
  const profile = session.profile
  if (!profile) return null
  // Pages of the areas kept out of the navigation (Settings); appearance has its own item.
  const settings = session.menuAreas.filter((area) => area.inAccountMenu).map((area) => ({
    area,
    pages: flatten(area.children, [area.id])
      .map(({ entry }) => ({ entry, item: itemsByPath.get(entry.path) }))
      .filter(({ entry, item }) => entry.path !== '/administration/appearance' && item && entryVisible(entry, session) && isOpenable(item, session)),
  }))
  // Where the user works: «Branch – Company», or «Company – Вся компания» for a company-wide sign-in.
  const company = session.currentCompany && pickName(session.currentCompany, ui.contentLocale)
  const place = (session.activeBranch ? [pickName(session.activeBranch, ui.contentLocale), company] : [company, t('common.companyWide')]).filter(Boolean).join(' – ')
  return (
    <>
      <Popover
        align="end"
        trigger={({ toggle, ...aria }) => (
          <button
            type="button"
            onClick={() => toggle()}
            {...aria}
            aria-label={t('layout.topbar.accountMenu')}
            className="flex h-9 items-center gap-2 rounded-full py-0.5 pl-0.5 pr-2 transition-colors hover:bg-hover"
          >
            <Avatar name={profile.display_name} src={session.avatarUrl} size={30} />
            <span className="hidden max-w-[140px] flex-col text-left leading-tight xl:flex">
              <span className="truncate text-[length:calc(13px*var(--text-scale))] font-medium text-fg">{profile.display_name}</span>
              {place && <span className="truncate text-[length:calc(11.5px*var(--text-scale))] text-muted">{place}</span>}
            </span>
          </button>
        )}
      >
        {(close) => (
          <div className="w-64">
            <div className="flex items-center gap-3 px-2.5 pb-2.5 pt-1.5">
              <Avatar name={profile.display_name} src={session.avatarUrl} size={36} />
              <div className="min-w-0">
                <div className="truncate text-[length:calc(13.5px*var(--text-scale))] font-medium text-fg">{profile.display_name}</div>
                {place && (
                  <div className="truncate text-[length:calc(12px*var(--text-scale))] text-muted" title={place}>
                    {place}
                  </div>
                )}
              </div>
            </div>
            {/* The own card needs no Users right: /me/account acts only on the caller. */}
            <MenuItem
              icon={<UserRound className="size-4" />}
              onSelect={() => {
                close()
                setAccountDialog(({ key }) => ({ open: true, key: key + 1 }))
              }}
            >
              {t('layout.myAccount.open')}
            </MenuItem>
            <MenuSeparator />
            <MenuLabel>{t('layout.topbar.appearance')}</MenuLabel>
            <ThemeSwitch />
            <MenuItem
              icon={<Paintbrush className="size-4" />}
              onSelect={() => {
                close()
                navigate('/administration/appearance')
              }}
            >
              {t('nav.items.appearance')}
            </MenuItem>
            {settings.map(({ area, pages }) =>
              pages.length === 0 ? null : (
                <div key={area.id}>
                  <MenuSeparator />
                  <MenuLabel>{menuText(area, ui.contentLocale)}</MenuLabel>
                  {pages.map(({ entry, item }) => {
                    const Icon = item!.icon
                    return (
                      <MenuItem
                        key={`${entry.path}?${entry.search ?? ''}`}
                        icon={<Icon className="size-4" />}
                        onSelect={() => {
                          close()
                          navigate({ pathname: entry.path, search: entry.search ? `?${entry.search}` : '' })
                        }}
                      >
                        {menuText(entry, ui.contentLocale)}
                      </MenuItem>
                    )
                  })}
                </div>
              ),
            )}
            <MenuSeparator />
            <MenuItem
              icon={<LogOut className="size-4" />}
              danger
              onSelect={() => {
                close()
                void auth.logout()
              }}
            >
              {t('common.actions.signOut')}
            </MenuItem>
          </div>
        )}
      </Popover>
      <MyAccountDialog key={accountDialog.key} open={accountDialog.open} onClose={() => setAccountDialog(({ key }) => ({ open: false, key }))} />
    </>
  )
})

const LocaleSwitcher = observer(function LocaleSwitcher() {
  const t = useT()
  const { ui } = useStores()
  return (
    <Popover
      align="end"
      trigger={({ toggle, ...aria }) => (
        <Button variant="ghost" size="sm" onClick={() => toggle()} {...aria} aria-label={t('layout.topbar.language')} title={t('layout.topbar.language')} icon={<Languages className="size-4" />}>
          <span className="text-[length:calc(12px*var(--text-scale))] font-semibold uppercase tracking-[0.04em]">{ui.contentLocale}</span>
        </Button>
      )}
    >
      {(close) => (
        <div className="w-56">
          <MenuLabel>{t('layout.topbar.language')}</MenuLabel>
          {CONTENT_LOCALES.map((locale) => (
            <MenuItem
              key={locale}
              onSelect={() => {
                ui.setContentLocale(locale)
                close()
              }}
              trailing={ui.contentLocale === locale ? <Check className="size-3.5 text-accent" /> : <span className="text-[length:calc(11px*var(--text-scale))] uppercase text-subtle">{locale}</span>}
            >
              {LOCALE_NAMES[locale]}
            </MenuItem>
          ))}
          <p className="px-2.5 pb-1 pt-2 text-[length:calc(12px*var(--text-scale))] text-muted">{t('layout.topbar.languageHint')}</p>
        </div>
      )}
    </Popover>
  )
})

/** Branch, notifications, language, theme and account: shared by the top bar and the top menu row. */
export const TopbarActions = observer(function TopbarActions() {
  const t = useT()
  const { ui } = useStores()
  return (
    <div className="ml-auto flex shrink-0 items-center gap-1">
      <CompanySwitcher />
      <BalanceSwitcher />
      <BranchSwitcher />
      <div className="mx-1 hidden h-5 w-px bg-line md:block" />
      <GlobalRefresh />
      <OnlineUsers />
      <ChatButton />
      <NotificationBell />
      <LocaleSwitcher />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => ui.cycleTheme()}
        aria-label={ui.resolvedTheme === 'dark' ? t('layout.topbar.switchToLight') : t('layout.topbar.switchToDark')}
        title={t('layout.topbar.toggleTheme')}
      >
        {ui.resolvedTheme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </Button>
      <UserMenu />
    </div>
  )
})

/** The search field as one icon, for the single-row top menu. */
export const SearchButton = observer(function SearchButton() {
  const t = useT()
  const { ui } = useStores()
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => ui.setCommandOpen(true)}
      aria-label={t('layout.topbar.search')}
      title={`${t('layout.topbar.search')} ${isMac ? '⌘' : 'Ctrl+'}K`}
    >
      <Search className="size-4" />
    </Button>
  )
})

export const Topbar = observer(function Topbar() {
  const t = useT()
  const { ui } = useStores()
  return (
    <header className="glass glass-sheen flex h-14 shrink-0 items-center gap-2 rounded-[var(--radius-shell)] px-2.5">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => ui.setMobileNav(true)} aria-label={t('layout.sidebar.openNavigation')}>
        <Menu className="size-4.5" />
      </Button>

      <button
        type="button"
        onClick={() => ui.setCommandOpen(true)}
        className="flex h-9 w-full max-w-[420px] items-center gap-2.5 rounded-[calc(11px*var(--radius-scale))] border border-field-border bg-field px-3 text-left text-[length:calc(13.5px*var(--text-scale))] text-subtle transition-colors hover:border-line-strong hover:text-muted"
      >
        <Search className="size-4 shrink-0" />
        <span className="flex-1 truncate">{t('layout.topbar.search')}</span>
        <span className="hidden gap-1 sm:flex">
          <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <TopbarActions />
    </header>
  )
})
